import type { Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import { cn } from "@chams-pay/design-system/lib/utils";
import { Copy } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

/*
 * One vocabulary for empty, failed and loading, used by every operational
 * screen. Failures are always scoped to the card that failed and carry their
 * own retry: the rest of the page keeps working.
 */

function StateCard({
  tone,
  glyph,
  title,
  body,
  action,
  className,
  testId,
}: {
  readonly tone: "neutral" | "success" | "danger";
  readonly glyph: ReactNode;
  readonly title: string;
  readonly body?: string;
  readonly action?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}) {
  return (
    <div
      data-tone={tone}
      {...(testId ? { "data-testid": testId } : {})}
      className={cn(
        "state-card flex flex-col items-center gap-2.5 rounded-lg border border-border bg-card p-6 text-center",
        tone === "danger" && "border-destructive-border",
        className,
      )}
    >
      <span
        aria-hidden="true"
        data-tone={tone}
        className={cn(
          "grid size-11 place-items-center rounded-full",
          tone === "success" && "bg-success-soft text-success",
          tone === "danger" && "bg-[#fdf0ef] text-destructive",
          tone === "neutral" && "bg-canvas text-muted-2",
        )}
      >
        {glyph}
      </span>
      <strong className="font-heading text-sm font-bold text-foreground">{title}</strong>
      {body && (
        <span className="max-w-[34ch] text-xs leading-relaxed text-muted-foreground">{body}</span>
      )}
      {action}
    </div>
  );
}

function BangGlyph() {
  return <span className="font-heading text-xl font-extrabold leading-none">!</span>;
}

export function CheckCircleGlyph() {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      className="size-5"
    >
      <path d="M4 9.3l3.2 3.2L14 5.8" />
    </svg>
  );
}

export function CalendarGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      className={cn("size-5", className)}
    >
      <rect x="2.5" y="3.5" width="13" height="12" rx="2" />
      <path d="M2.5 7h13M6 2v3M12 2v3" />
    </svg>
  );
}

export function ChequeGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
      className={cn("size-4 text-muted-2", className)}
    >
      <rect x="1.5" y="4" width="15" height="10" rx="2" />
      <path d="M4.5 9h5" />
    </svg>
  );
}

export function TransferGlyph({ className }: { readonly className?: string }) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
      className={cn("size-4 text-muted-2", className)}
    >
      <path d="M3 6.5h12l-3-3M15 11.5H3l3 3" />
    </svg>
  );
}

/** The empty state every list falls back to when it has nothing to show. */
export function GenericEmpty({
  locale,
  className,
}: {
  readonly locale: Locale;
  readonly className?: string;
}) {
  return (
    <StateCard
      tone="neutral"
      className={cn(className)}
      glyph={
        <span
          className="chams-logo size-5"
          style={{ background: "rgb(var(--disabled))" }}
          aria-hidden="true"
        />
      }
      title={translate(locale, "ops.genericEmpty")}
      body={translate(locale, "ops.genericEmptyBody")}
    />
  );
}

/** The scoped failure card: the failing section only, with its own retry. */
export function ScopedError({
  locale,
  title,
  body,
  onRetry,
  className,
}: {
  readonly locale: Locale;
  readonly title?: string;
  readonly body?: string;
  readonly onRetry?: () => void;
  readonly className?: string;
}) {
  return (
    <StateCard
      tone="danger"
      className={cn(className)}
      glyph={<BangGlyph />}
      title={title ?? translate(locale, "ops.genericError")}
      body={body ?? translate(locale, "ops.genericErrorBody")}
      action={
        onRetry ? (
          <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={onRetry}>
            {translate(locale, "ops.retry")}
          </Button>
        ) : undefined
      }
    />
  );
}

/** A scoped empty state with its own copy (queue cleared, empty period…). */
export function ScopedEmpty({
  tone = "neutral",
  glyph,
  title,
  body,
  className,
}: {
  readonly tone?: "neutral" | "success";
  readonly glyph: ReactNode;
  readonly title: string;
  readonly body?: string;
  readonly className?: string;
}) {
  return (
    <StateCard
      tone={tone}
      glyph={glyph}
      title={title}
      {...(body ? { body } : {})}
      {...(className ? { className } : {})}
    />
  );
}

/** The generic loading placeholder: final geometry, opacity-only shimmer. */
export function SkeletonLines({ className }: { readonly className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2.5", className)} aria-hidden="true">
      <span className="chams-skeleton block h-2.5 w-1/2" />
      <span className="chams-skeleton block h-2.5 w-5/6" />
      <span className="chams-skeleton block h-2.5 w-2/3" />
    </div>
  );
}

export function Spinner({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block size-3.5 animate-spin rounded-full border-2 border-border border-t-primary",
        className,
      )}
    />
  );
}

/** Seconds left until an instant the server supplied; null when it has none. */
function useRemainingSeconds(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const target = Date.parse(expiresAt);
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.round((target - now) / 1000));
}

function clock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * The issued access code: high entropy, shown once, on ink so it reads as a
 * secret. The countdown appears only when the server returned an expiry.
 */
export function AccessCodeReveal({
  locale,
  code,
  expiresAt,
  className,
  onCopied,
}: {
  readonly locale: Locale;
  readonly code: string;
  readonly expiresAt: string | null;
  readonly className?: string;
  readonly onCopied?: () => void;
}) {
  const remaining = useRemainingSeconds(expiresAt);

  return (
    <div
      className={cn(
        "issued-code flex flex-col gap-3 rounded-lg bg-[#101c1e] p-4 text-white",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <bdi
          dir="ltr"
          className="chams-figure min-w-0 flex-1 break-all font-heading text-lg font-extrabold tracking-[0.14em] text-white"
        >
          {code}
        </bdi>
        {remaining !== null && (
          <span className="flex flex-col items-end gap-1">
            <span className="text-[10px] text-white/60">{translate(locale, "ops.expiresIn")}</span>
            <bdi dir="ltr" className="chams-figure text-xs font-bold text-[#7de2a8]">
              {clock(remaining)}
            </bdi>
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11 bg-white/15 text-white hover:bg-white/25"
          onClick={() =>
            void navigator.clipboard
              .writeText(code)
              .then(onCopied)
              .catch(() => undefined)
          }
        >
          <Copy aria-hidden="true" />
          {translate(locale, "ops.copy")}
        </Button>
      </div>
      <span className="text-[11px] leading-relaxed text-white/70">
        {translate(locale, "ops.codeOnceNote")}
      </span>
    </div>
  );
}
